import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Store, ArrowRight, ShieldCheck } from "lucide-react";
import AppShell from "@/components/AppShell";

export default function SignUp() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [business, setBusiness] = useState("");

  const canContinue = email.trim().length > 3 && business.trim().length > 1;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canContinue) return;
    try {
      sessionStorage.setItem(
        "ta-signup-draft",
        JSON.stringify({ email: email.trim(), businessName: business.trim() })
      );
    } catch (_) { /* sessionStorage may be blocked; onboarding will still work */ }
    navigate("/onboarding");
  };

  return (
    <AppShell showBack backTo="/" testId="signup-page">
      <div className="pt-6 sm:pt-10 max-w-lg">
        <div className="text-xs uppercase tracking-widest font-medium mb-3" style={{ color: "var(--ta-secondary)" }}>
          Free 30-Day Monday Brief Trial
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]" style={{ color: "var(--ta-primary)" }} data-testid="signup-headline">
          Let's get you set up.
        </h1>
        <p className="mt-4 text-lg leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>
          Two quick things and we'll have your first Monday Brief ready.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="signup-form">
          <Field
            label="Your email"
            icon={Mail}
            testId="signup-email-input"
            type="email"
            placeholder="you@yourshop.com"
            value={email}
            onChange={setEmail}
          />
          <Field
            label="Business name"
            icon={Store}
            testId="signup-business-input"
            placeholder="Ridgeline Plumbing Co."
            value={business}
            onChange={setBusiness}
          />

          <button
            type="submit"
            disabled={!canContinue}
            data-testid="signup-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 min-h-[64px] rounded-2xl text-lg font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
            style={{ background: "var(--ta-primary)" }}
          >
            Continue to Setup <ArrowRight size={20} />
          </button>

          <p className="text-sm inline-flex items-center gap-2 mt-2" style={{ color: "var(--ta-text-secondary)" }}>
            <ShieldCheck size={16} style={{ color: "var(--ta-success)" }} />
            No credit card required. We'll never share your info.
          </p>
        </form>
      </div>
    </AppShell>
  );
}

const Field = ({ label, icon: Icon, testId, value, onChange, ...rest }) => (
  <label className="block">
    <span className="text-sm font-medium mb-2 inline-block" style={{ color: "var(--ta-primary)" }}>
      {label}
    </span>
    <div
      className="flex items-center gap-3 px-4 rounded-2xl bg-white border-2 transition-colors focus-within:border-[var(--ta-primary)]"
      style={{ borderColor: "var(--ta-border)", minHeight: 64 }}
    >
      <Icon size={20} strokeWidth={2} style={{ color: "var(--ta-primary)" }} />
      <input
        {...rest}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full h-full text-lg bg-transparent outline-none py-4"
        style={{ color: "var(--ta-text)" }}
      />
    </div>
  </label>
);
